/**
 * AgentBox — pick the referring agent (none / registered / unregistered)
 * and compute the commission preview.
 * Extracted from CompanyRegistrationDetailPage.jsx during the iter-238 refactor.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Loader2, X, CheckCircle2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { floorAED } from '../../utils/money';
import { DirhamPrice } from '../../components/DirhamPrice';
import { SectionCard, SmartSaveButton } from './ui';
import { API, authHeader } from './helpers';

export function AgentBox({ reg, onSave, t, saving }) {
  const a = reg.agent || { mode: 'none' };
  // iter-237 (Feb 2026) — no default. `mode` stays null until user picks.
  const agentSaved = !!(reg.agent && reg.agent.mode);
  const [mode, setMode] = useState(agentSaved ? a.mode : null);
  const [name, setName] = useState(a.name || '');
  const [email, setEmail] = useState(a.email || '');
  const [phone, setPhone] = useState(a.phone || '');
  const [pickedId, setPickedId] = useState(a.id || '');
  const [percent, setPercent] = useState(a.commission_percent || '');
  const [agentsList, setAgentsList] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  // iter-231 (Feb 2026) — user asked to be able to SEARCH the agents list
  // (was a bare <select> with all rows dumped in one dropdown).
  const [searchQuery, setSearchQuery] = useState('');

  // Load agents when switching to registered
  useEffect(() => {
    if (mode !== 'registered' || agentsList.length > 0) return;
    (async () => {
      setLoadingAgents(true);
      try {
        const res = await axios.get(API + '/api/admin/agents', { headers: authHeader() });
        setAgentsList((res.data && (res.data.agents || res.data.data || res.data)) || []);
      } catch {
        // silent
      } finally {
        setLoadingAgents(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // iter-231 — Filter agents list by search query (name / email / phone).
  const filteredAgents = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return agentsList;
    return agentsList.filter((ag) => {
      const bits = [
        ag.name, ag.full_name, ag.email, ag.phone,
      ].filter(Boolean).map((s) => String(s).toLowerCase());
      return bits.some((b) => b.includes(q));
    });
  }, [agentsList, searchQuery]);

  const save = () => {
    if (!mode) {
      toast.error(t('اختر: بدون وكيل / وكيل مسجل / وكيل غير مسجل', 'Pick: No agent / Registered / Unregistered'));
      return;
    }
    const payload = { agent: { mode } };
    if (mode === 'registered') {
      // iter-299h — When the operator picks "وكيل مسجل" (Registered
      // agent) we must have an actual agent chosen from the list.
      // Previously the Save button would accept an empty pick and
      // silently persist mode='registered' without an id, which looked
      // like a successful save but left the agent record incomplete.
      if (!pickedId) {
        toast.error(t('اختر الوكيل من القائمة قبل الحفظ', 'Pick an agent from the list before saving'));
        return;
      }
      if (!percent || parseFloat(percent) <= 0) {
        toast.error(t('أدخل نسبة عمولة الوكيل', "Enter the agent's commission percentage"));
        return;
      }
      const found = agentsList.find((x) => (x.id || x._id) === pickedId);
      payload.agent.id = pickedId;
      payload.agent.name = found ? (found.name || found.full_name || found.email) : name;
      payload.agent.email = found ? found.email : email;
      payload.agent.phone = found ? (found.phone || '') : phone;
      payload.agent.commission_percent = parseFloat(percent) || 0;
    } else if (mode === 'unregistered') {
      // iter-299h — Unregistered agents must at minimum have a NAME + a
      // way to contact them (phone or email), otherwise there's no
      // record left of who referred the registration.
      if (!name || !name.trim()) {
        toast.error(t('أدخل اسم الوكيل غير المسجل', "Enter the unregistered agent's name"));
        return;
      }
      if ((!phone || !phone.trim()) && (!email || !email.trim())) {
        toast.error(t('أدخل هاتف أو بريد الوكيل غير المسجل', "Enter phone or email for the unregistered agent"));
        return;
      }
      if (!percent || parseFloat(percent) <= 0) {
        toast.error(t('أدخل نسبة عمولة الوكيل', "Enter the agent's commission percentage"));
        return;
      }
      payload.agent.name = name.trim();
      payload.agent.email = (email || '').trim();
      payload.agent.phone = (phone || '').trim();
      payload.agent.commission_percent = parseFloat(percent) || 0;
    }
    onSave(payload);
  };

  // iter-264c — dirty tracking for the SmartSaveButton.
  const dirty = useMemo(() => {
    const savedMode = agentSaved ? (a.mode || null) : null;
    if ((mode || null) !== savedMode) return true;
    if (mode === 'registered') {
      return (
        (pickedId || '') !== (a.id || '') ||
        String(percent || '') !== String(a.commission_percent || '')
      );
    }
    if (mode === 'unregistered') {
      return (
        (name  || '') !== (a.name  || '') ||
        (email || '') !== (a.email || '') ||
        (phone || '') !== (a.phone || '') ||
        String(percent || '') !== String(a.commission_percent || '')
      );
    }
    return false;
  }, [mode, pickedId, percent, name, email, phone, agentSaved, a.mode, a.id, a.name, a.email, a.phone, a.commission_percent]);

  // iter-234 (Feb 2026, revised) — Commission base is DERIVED from the
  // fee-collection mode selected on this request:
  //   • mode = "commission"  → base = license fees, label = "سعر الرخصة"
  //     (we take a % of the license fee as our agent commission)
  //   • mode = "admin_fees"  → base = admin fees, label = "صافي الربح"
  //     (we take a % of what we net directly from the customer)
  const afMode = reg?.admin_fees?.mode || 'admin_fees';
  const commissionBase = afMode === 'commission'
    ? Number(reg?.license_fees?.amount || 0)
    : Number(reg?.admin_fees?.amount   || 0);
  const baseLabel = afMode === 'commission'
    ? t('سعر الرخصة', 'License fee')
    : t('صافي الربح', 'Net profit');
  const previewCommission = mode !== 'none' && percent && commissionBase
    ? floorAED(commissionBase * parseFloat(percent) / 100).toFixed(2)
    : null;

  return (
    <SectionCard icon={User} iconColor="#10b981" title={t('الوكيل', 'Agent')}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {['none', 'registered', 'unregistered'].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={
              'p-3 rounded-xl border-2 text-sm font-semibold transition-all ' +
              (mode === m
                ? 'bg-emerald-500/15 border-emerald-500 text-emerald-300'
                : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
            }
            data-testid={'agent-mode-' + m}
          >
            {m === 'none' ? t('بدون وكيل', 'No agent')
              : m === 'registered' ? t('وكيل مسجل', 'Registered agent')
              : t('وكيل غير مسجل', 'Unregistered agent')}
          </button>
        ))}
      </div>

      {mode === 'registered' && (
        <div className="grid grid-cols-1 gap-4 mb-4">
          <div>
            <label className="block text-xs text-[#71717A] mb-2">{t('ابحث عن الوكيل', 'Search for agent')}</label>
            {loadingAgents ? <Loader2 className="h-4 w-4 animate-spin text-[#71717A]" /> : (
              <>
                {/* iter-231d — Autocomplete pattern per user request:
                     - No list shown until admin types at least 1 character.
                     - As admin types, matching agents appear below the input.
                     - Clicking a suggestion selects the agent and hides the list. */}
                <div className="relative">
                  <Input
                    placeholder={t('اكتب الاسم أو البريد أو الهاتف…', 'Type name / email / phone…')}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      // typing invalidates any prior pick
                      if (pickedId) setPickedId('');
                    }}
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl pr-10"
                    data-testid="agent-search-input"
                    autoComplete="off"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => { setSearchQuery(''); setPickedId(''); }}
                      className="absolute inset-y-0 left-2 flex items-center text-[#71717A] hover:text-white"
                      data-testid="agent-search-clear"
                      aria-label={t('مسح', 'Clear')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {/* Suggestions only appear WHEN user is typing AND no agent picked yet */}
                {searchQuery.trim().length > 0 && !pickedId && (
                  <div
                    className="mt-1 max-h-64 overflow-y-auto rounded-xl border border-[#2a2a4e] bg-[#0a0a0a] divide-y divide-white/5"
                    data-testid="agent-picker-suggestions"
                  >
                    {filteredAgents.length === 0 ? (
                      <div className="p-3 text-center text-xs text-[#71717A]">
                        {t('لا يوجد وكيل مطابق', 'No matching agent')}
                      </div>
                    ) : (
                      filteredAgents.map((ag) => {
                        const agId = ag.id || ag._id;
                        return (
                          <button
                            key={agId}
                            type="button"
                            onClick={() => {
                              setPickedId(agId);
                              setSearchQuery(ag.name || ag.full_name || ag.email);
                            }}
                            data-testid={`agent-picker-row-${agId}`}
                            className="w-full text-right p-3 hover:bg-white/5 transition"
                          >
                            <div className="text-sm font-semibold text-white truncate">
                              {ag.name || ag.full_name || ag.email}
                            </div>
                            <div className="text-xs text-[#71717A] truncate" dir="ltr">
                              {ag.email}
                              {ag.phone ? <span className="mx-1">·</span> : null}
                              {ag.phone || ''}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
                {/* Selected agent chip */}
                {pickedId && (() => {
                  const picked = agentsList.find((x) => (x.id || x._id) === pickedId);
                  if (!picked) return null;
                  return (
                    <div className="mt-2 flex items-center justify-between gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30" data-testid="agent-picker-selected">
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-emerald-200 truncate">
                            {picked.name || picked.full_name || picked.email}
                          </div>
                          <div className="text-xs text-emerald-300/70 truncate" dir="ltr">{picked.email}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setPickedId(''); setSearchQuery(''); }}
                        className="text-xs text-[#71717A] hover:text-white"
                        data-testid="agent-picker-clear-btn"
                      >
                        {t('تغيير', 'Change')}
                      </button>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}

      {mode === 'unregistered' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <Input placeholder={t('الاسم', 'Name')} value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" />
          <Input placeholder={t('البريد', 'Email')} value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" />
          <Input placeholder={t('الهاتف', 'Phone')} value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" />
        </div>
      )}

      {mode !== 'none' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 pt-4 border-t border-white/5">
          <div>
            <label className="block text-xs text-[#71717A] mb-2">{baseLabel}</label>
            <div className="px-3 py-2 rounded-xl bg-black/40 border border-[#2a2a4e] text-white font-mono" dir="ltr" data-testid="agent-commission-base">
              {commissionBase || '—'}
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#71717A] mb-2">{t('نسبة العمولة %', 'Commission %')}</label>
            <Input type="number" value={percent} onChange={(e) => setPercent(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" />
          </div>
          <div>
            <label className="block text-xs text-[#71717A] mb-2">{t('عمولة الوكيل المحسوبة', 'Computed commission')}</label>
            <div className="px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 font-mono font-bold" dir="ltr">
              {previewCommission ? <DirhamPrice value={parseFloat(previewCommission)} decimals={2} /> : '—'}
            </div>
          </div>
        </div>
      )}

      <div className="text-end">
        <SmartSaveButton
          dirty={dirty}
          saving={saving}
          onClick={save}
          t={t}
          color="#10b981"
          hoverColor="#059669"
          data-testid="agent-save"
        />
      </div>
    </SectionCard>
  );
}
